import dayjs from "dayjs";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getFormattedValue, hasSelectedChartData } from "./utils";
import { useCallback, useMemo } from "react";
import {
  CHART_DIMENSIONS,
  DATE_FORMAT,
  getLineConfig,
  getMeasureColor,
  getYAxisConfig,
} from "../../config/charts/sensorChart.config";
import NoChartDataEmptyState from "../emptyStates/NoChartDataEmptyState.component";

const SensorAverageChart = ({
  chartData,
  selectedMeasures,
  paramsList,
  isLoading,
  error,
}) => {
  const formatXAxis = useCallback((timestamp) => {
    return dayjs(timestamp).format(DATE_FORMAT);
  }, []);
  const hasData = hasSelectedChartData(chartData, selectedMeasures);

  const measureKeyToLabelMap = useMemo(() => {
    return paramsList.reduce((map, measure) => {
      map[measure.value] = measure;
      return map;
    }, {});
  }, [paramsList]);

  const tooltipFormatter = useCallback(
    (value, name, entry) => {
      const metadata = measureKeyToLabelMap[entry?.dataKey] || measureKeyToLabelMap[name];
      const label = metadata?.label || name;
      return [getFormattedValue(value, name, metadata?.unit), label];
    },
    [measureKeyToLabelMap]
  );

  const legendFormatter = useCallback(
    (value) => measureKeyToLabelMap[value]?.label || value,
    [measureKeyToLabelMap]
  );

  if (!hasData && !isLoading) {
    return error ? null : <NoChartDataEmptyState />;
  }

  return (
    <ResponsiveContainer width="100%" height={300} minHeight={300}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="timestamp"
          tickFormatter={formatXAxis}
          angle={CHART_DIMENSIONS.xAxisAngle}
          textAnchor="end"
          height={CHART_DIMENSIONS.xAxisHeight}
        />
        <Tooltip labelFormatter={formatXAxis} formatter={tooltipFormatter} />
        <Legend formatter={legendFormatter} />
        {selectedMeasures.map((measureKey, index) => {
          const color = getMeasureColor(measureKey);
          const yAxisConfig = getYAxisConfig(measureKey, index, color, measureKeyToLabelMap[measureKey]?.unit);

          return <YAxis key={`yAxis-${measureKey}`} {...yAxisConfig} />;
        })}
        {selectedMeasures.map((measureKey, index) => {
          const color = getMeasureColor(measureKey);
          const label = measureKeyToLabelMap[measureKey]?.label || measureKey;
          const lineConfig = getLineConfig(measureKey, label, color);

          return <Line key={measureKey} {...lineConfig} />;
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default SensorAverageChart;
